import React, { useRef, useEffect, useMemo, useState, Fragment } from 'react';
import urlParser from 'js-video-url-parser';
import YoutubeEmbed from 'components/YoutubeEmbed/YoutubeEmbed';
import VimeoEmbed from 'components/VimeoEmbed/VimeoEmbed';
import useStyle from './style';
import styles from 'assets/styles/default.scss';
import { createMuiTheme, useMediaQuery } from '@material-ui/core';
import { CSSProperties } from '@material-ui/core/styles/withStyles';

export const YOUTUBE_PROVIDER = 'youtube';
export const VIMEO_PROVIDER = 'vimeo';

export interface PlayerVideoInfo {
  mediaType: string;
  id: string;
  provider: string;
}

export interface PlayerParserResult extends VideoInfo { 
  list?: string;
  params?: any;
}

interface PlayerCreateProps {
  videoInfo: PlayerVideoInfo,
  format: string,
  params?: any,
}

interface Props {
  url: string;
  autoPlay?: boolean;
  raw?: boolean;
  allowMinimize?: boolean;
}

export const isMobileMediaQuery: string = '(max-width: ' + styles.smSize + ')';

export default function ({
  url,
  autoPlay,
  raw,
  allowMinimize,
}: Props) {
  const classes = useStyle();
  const wrapperRef = useRef<null | HTMLDivElement>(null);
  const playerRef = useRef<null | HTMLDivElement>(null);
  const theme = createMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const [ miniPlayer, setMiniPlayer ] = useState<boolean>(false);

  const playerStyle = useMemo<CSSProperties>(() => {
    let obj: CSSProperties = {};
    const wrapperElement = wrapperRef.current;
    if (!!wrapperElement) {
      const wrapperComputedStyle: CSSStyleDeclaration = window.getComputedStyle(wrapperElement)
      obj.maxWidth = wrapperComputedStyle.width;
      if (miniPlayer) {
        obj.position = 'fixed';
        obj.top = `${wrapperElement.offsetTop}px`;
        obj.left = `${wrapperElement.offsetLeft}px`;
      }
    }
    return obj;
  }, [wrapperRef, miniPlayer])

  useEffect(() => {
    if (allowMinimize && !isMobile) {
      const element = wrapperRef.current;
      if (!!element) {
        const toggleSize = () => {
          const needToggle = document.documentElement.scrollHeight > window.innerHeight + 84;
          if (!element || !needToggle) return;
          if (window.scrollY >= 32) {
            if (!miniPlayer) {
              setMiniPlayer(true);
            }
          } else if (window.scrollY === 0) {
            if (miniPlayer) {
              setMiniPlayer(false);
            }
            if (!!(element.nextElementSibling as HTMLElement)) {
              (element.nextElementSibling as HTMLElement).removeAttribute('style');
            }
            element.classList.remove(classes.miniPlayer);
          }
        };
        window.addEventListener('scroll', toggleSize);
        return () => {
          window.removeEventListener('scroll', toggleSize);
        };
      }
    }
  }, [wrapperRef, classes, allowMinimize, isMobile, miniPlayer, setMiniPlayer]);
  
  const parserResult = useMemo<PlayerParserResult>(() => {
    return urlParser.parse(url);
  }, [url])

  const videoId = useMemo<string | null>(() => {
    return !!parserResult ? parserResult.id : null;
  }, [parserResult])

  const videoProvider = useMemo<string | null>(() => {
    return !!parserResult ? parserResult.provider : null;
  }, [parserResult])

  if (!parserResult || !videoId || !videoProvider) {
    return <span></span>
  }

  switch(videoProvider) {
    case YOUTUBE_PROVIDER:
    case VIMEO_PROVIDER:
      return (
        <Fragment>
          <div
            className={classes.wrapper}
            ref={wrapperRef}
          >
            <div
              className={[
                classes.player,
                miniPlayer ? classes.miniPlayer : ''
              ].join(' ')}
              style={playerStyle}
              ref={playerRef}
            >
              {
                videoProvider === YOUTUBE_PROVIDER && (<YoutubeEmbed id={videoId} autoPlay={autoPlay} raw={raw} />)
              }
              {
                videoProvider === VIMEO_PROVIDER && (<VimeoEmbed id={videoId} autoPlay={autoPlay} raw={raw} />)
              }
            </div>
          </div>
        </Fragment>
      )
    default:
      return <span></span>;
  }
}
